import * as React from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  NarrativeTextArea,
  type Abbreviation,
} from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import {
  type RestrictedWordMatch,
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";

// ── Types ──────────────────────────────────────────────────────────

export type RestrictedWordDialogProps = {
  open: boolean;
  narrative: string;
  date: Date;
  matterId?: string;
  restrictedWords?: RestrictedWord[];
  abbreviations?: Abbreviation[];
  onBack: () => void;
  onSave: (updatedNarrative: string) => void;
};

// ── Helpers ─────────────────────────────────────────────────────────

function uniqueMatches(matches: RestrictedWordMatch[]): RestrictedWordMatch[] {
  const seen = new Set<string>();
  return matches.filter((m) => {
    const key = `${m.text.toLowerCase()}-${m.severity}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Component ──────────────────────────────────────────────────────

export function RestrictedWordDialog({
  open,
  narrative,
  date,
  matterId,
  restrictedWords,
  abbreviations,
  onBack,
  onSave,
}: RestrictedWordDialogProps) {
  const { makeApiRequest } = useApi();
  const [localNarrative, setLocalNarrative] = React.useState(narrative);
  const [isFixing, setIsFixing] = React.useState(false);

  // Sync when dialog opens or external narrative changes
  React.useEffect(() => {
    if (open) setLocalNarrative(narrative);
  }, [open, narrative]);

  const matches = React.useMemo(
    () =>
      restrictedWords?.length
        ? mergeOverlappingMatches(
            detectRestrictedWords(localNarrative, restrictedWords),
          )
        : [],
    [localNarrative, restrictedWords],
  );

  const blockMatches = uniqueMatches(
    matches.filter((m) => m.severity === "block"),
  );
  const warnMatches = uniqueMatches(
    matches.filter((m) => m.severity === "warning"),
  );
  const hasBlocking = blockMatches.length > 0;

  const handleFixWithAI = React.useCallback(async () => {
    setIsFixing(true);

    try {
      const response = await makeApiRequest({
        endpoint: "/records/restrictedWords",
        method: "POST",
        body: {
          description: localNarrative,
          date,
          matter_id: matterId,
        },
      });

      if (response.error) {
        throw new Error(response.error);
      }

      if (
        !response.data ||
        typeof response.data !== "string" ||
        !response.data.trim()
      ) {
        throw new Error("Invalid response from restricted words API");
      }

      setLocalNarrative(response.data);

      toast.success("Restricted words have been automatically replaced!", {
        position: "top-center",
      });
    } catch (error) {
      logger.error("Restricted words fix failed", {
        error: error instanceof Error ? error.message : String(error),
        narrativeLength: localNarrative.length,
      });
      if (error instanceof Error) {
        logger.captureException(error, { action: "fixRestrictedWords" });
      }
      toast.error("Failed to fix restricted words. Please try manually.", {
        position: "top-center",
        duration: 5000,
      });
    } finally {
      setIsFixing(false);
    }
  }, [localNarrative, date, matterId, makeApiRequest]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onBack();
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        onEscapeKeyDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>Restricted words detected</DialogTitle>
          <DialogDescription>
            Your narrative contains restricted words. Edit the text below, use AI
            to fix them, or go back.
          </DialogDescription>
        </DialogHeader>

        {/* Editable narrative */}
        <NarrativeTextArea
          value={localNarrative}
          onChange={setLocalNarrative}
          restrictedWords={restrictedWords}
          abbreviations={abbreviations}
        />

        {/* Blocked words summary */}
        {blockMatches.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">
              Blocked — must fix ({blockMatches.length})
            </p>
            <div className="space-y-1.5">
              {blockMatches.map((m, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 px-3 py-2"
                >
                  <span className="shrink-0 inline-block h-2 w-2 mt-1.5 rounded-full bg-red-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{m.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.suggestion ? (
                        <>Suggested: <span className="font-medium text-foreground">{m.suggestion}</span></>
                      ) : (
                        "No replacement suggested"
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning words summary */}
        {warnMatches.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
              Warnings ({warnMatches.length})
            </p>
            <div className="space-y-1.5">
              {warnMatches.map((m, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 px-3 py-2"
                >
                  <span className="shrink-0 inline-block h-2 w-2 mt-1.5 rounded-full bg-amber-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{m.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.suggestion ? (
                        <>Suggested: <span className="font-medium text-foreground">{m.suggestion}</span></>
                      ) : (
                        "No replacement suggested"
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isFixing}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleFixWithAI}
              disabled={isFixing}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {isFixing ? "Fixing..." : "Fix with AI"}
            </Button>
            <Button
              type="button"
              onClick={() => onSave(localNarrative)}
              disabled={hasBlocking || isFixing}
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RestrictedWordDialog;
